<script setup lang="ts">
import { onMounted, ref } from 'vue'
import RoomieInput from '@lahaus-roomie/roomie/src/components/RoomieInput/index.vue'
import MixMaxRange from '@/components/App/MinMaxRange/index.vue'
import type { FormAreaRangeComponentModule } from '@/components/BrandedV2/FormSearchFilters/AreaRange/types'

withDefaults(defineProps<FormAreaRangeComponentModule.Props>(), {
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  }
})

const { t } = useI18n()
const emit = defineEmits(['change'])
const { $useLhId } = useNuxtApp()

const defaultRangeValues = {
  min: 1,
  max: 2000,
  interval: 1,
  gap: 5
}

const touchedInputs = ref(false)
const minArea = defineModel<number | undefined>('minArea', { default: null })
const maxArea = defineModel<number | undefined>('maxArea', { default: null })

const handleMinMaxChange = (event: { minValue: number, maxValue: number }) => {
  minArea.value = event.minValue
  maxArea.value = event.maxValue
  if (touchedInputs.value) emit('change', { min: event.minValue, max: event.maxValue })
  touchedInputs.value = false
}

onMounted(() => {
  if (minArea.value && maxArea.value) return

  minArea.value = defaultRangeValues.min
  maxArea.value = defaultRangeValues.max
})

const onInput = () => {
  touchedInputs.value = true
}

</script>

<script lang="ts">
export default {
  name: 'FormAreaRange'
}
</script>

<template>
  <ClientOnly>
    <div class="form-area-range pb-24">
      <div class="grid grid-cols-2 gap-12 mb-12">
        <RoomieInput
          :id="`${id}-min-area-input`"
          v-model="minArea"
          :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'escribir', C: 'area minima' })"
          :label="t('minLabel')"
          pattern="^\d*$"
          autocomplete="off"
          :is-disabled="false"
          @input="onInput" />

        <RoomieInput
          :id="`${id}-max-area-input`"
          v-model="maxArea"
          :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'escribir', C: 'area maxima' })"
          :label="t('maxLabel')"
          pattern="^\d*$"
          autocomplete="off"
          :is-disabled="false"
          @input="onInput" />
      </div>

      <MixMaxRange
        :id="`${id}-range-slider`"
        :initial-min="minArea"
        :initial-max="maxArea"
        :min-range="defaultRangeValues.min"
        :max-range="defaultRangeValues.max"
        :interval="defaultRangeValues.interval"
        :gap="defaultRangeValues.gap"
        :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, A: 'seleccionar rango de areas' }}"
        @input="onInput"
        @update:model-value="handleMinMaxChange" />
    </div>
  </ClientOnly>
</template>

<i18n lang="json" src="./i18n.json" />
